"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState, useMemo, useEffect } from "react";
import viugoFilterFlameIcon from "@/assets/images/viugo/kbv-v16-filter-flame-icon.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const THU_VI = [
  "Chủ nhật",
  "Thứ hai",
  "Thứ ba",
  "Thứ tư",
  "Thứ năm",
  "Thứ sáu",
  "Thứ bảy",
];

export default function USDateTabs({
  selectedDate,
  onDateChange,
  compact = false,
  isHotOnly = false,
  onToggleHot,
}) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const today = useMemo(() => dayjs().tz(TZ), []);
  const todayString = today.format("YYYY-MM-DD");

  const dateOptions = useMemo(() => {
    const options = [];

    options.push({
      value: "live",
      label: "Trực tuyến",
      dateLabel: null,
      shortDate: null,
    });

    options.push({
      value: todayString,
      label: "Hôm nay",
      dateLabel: today.format("DD/MM"),
      shortDate: today.format("DD-MM"),
    });

    const tomorrow = today.clone().add(1, "day");
    options.push({
      value: tomorrow.format("YYYY-MM-DD"),
      label: "Ngày mai",
      dateLabel: tomorrow.format("DD/MM"),
      shortDate: tomorrow.format("DD-MM"),
    });

    for (let i = 2; i <= 6; i++) {
      const d = today.clone().add(i, "day");
      options.push({
        value: d.format("YYYY-MM-DD"),
        label: THU_VI[d.day()],
        dateLabel: d.format("DD/MM"),
        shortDate: d.format("DD-MM"),
      });
    }

    return options;
  }, [todayString, today]);

  const selectedOpt = useMemo(() => {
    return dateOptions.find((o) => o.value === selectedDate) || dateOptions[0];
  }, [dateOptions, selectedDate]);

  useEffect(() => {
    if (isPopupOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isPopupOpen]);

  if (compact) {
    return (
      <div className="flex w-full items-center">
        <div
          onClick={(e) => {
            e.stopPropagation();
            setIsPopupOpen(true);
          }}
          className="relative flex h-[42px] grow cursor-pointer items-center justify-between rounded-2xl bg-[var(--theme-bg)] px-[12px] text-[14px]"
        >
          <div className="flex flex-row items-center gap-x-1 leading-tight">
            <span className="font-bold text-[var(--theme-text)]">{selectedOpt.label}</span>
            {selectedOpt.dateLabel && (
              <span className="text-[14px] text-[var(--theme-text-muted)]">
                ({selectedOpt.dateLabel})
              </span>
            )}
          </div>
          <span className="grid h-[28px] w-[28px] place-items-center rounded-[10px] bg-[var(--theme-accent)]">
            <svg
              className="h-4 w-4 frost-text-label-muted"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </div>

        {typeof onToggleHot === "function" ? (
          <button
            type="button"
            onClick={onToggleHot}
            className={`ml-2 inline-flex h-[42px] shrink-0 items-center justify-center gap-1 rounded-2xl border border-solid px-3 text-[13px] font-semibold ${
              isHotOnly
                ? "border-[var(--theme-live-chip-bg,var(--theme-primary))] bg-[var(--theme-accent)] text-[var(--theme-text)]"
                : "border-transparent bg-[var(--theme-accent)] text-[var(--theme-text)]"
            }`}
          >
            <img
              src={viugoFilterFlameIcon}
              alt=""
              width={18}
              height={18}
              className="shrink-0"
              aria-hidden
            />
            <span>Trận hot</span>
          </button>
        ) : null}

        <div
          className={`fixed inset-0 z-[999] transition-opacity duration-300 pointer-events-none ${
            isPopupOpen ? "opacity-100 pointer-events-auto" : "opacity-0"
          }`}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              setIsPopupOpen(false);
            }}
            className="absolute inset-0 bg-black/60"
            style={{ backdropFilter: "blur(8px)" }}
          />

          <div
            className={`absolute bottom-0 left-0 right-0 w-full transition-transform duration-300 ease-out transform ${
              isPopupOpen ? "translate-y-0" : "translate-y-full"
            }`}
            style={{ zIndex: 1000 }}
          >
            <div className="overflow-hidden rounded-t-2xl bg-[var(--theme-bg)] pb-10 shadow-[0_-4px_12px_rgba(0,0,0,0.35)]">
              <div className="relative flex items-center justify-center">
                <div className="py-4 text-[14px] font-semibold text-[var(--theme-text)]">
                  Chọn Ngày
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPopupOpen(false);
                  }}
                  className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg bg-[var(--theme-accent)]"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[var(--theme-text)]"
                  >
                    <path
                      d="M18 6L6 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                {dateOptions.map((opt) => {
                  const isSelected = !isHotOnly && selectedDate === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDateChange(opt.value);
                        setIsPopupOpen(false);
                      }}
                      className={`flex h-[44px] items-center gap-2 px-6 transition-colors ${
                        isSelected
                          ? "bg-[var(--theme-accent)]"
                          : "frost-date-option"
                      }`}
                    >
                      <div className="flex items-center text-[14px] leading-6">
                        <span
                          className={`${
                            opt.shortDate ? "w-[90px]" : ""
                          } inline-block text-[var(--theme-text)] font-semibold`}
                        >
                          {opt.label}
                        </span>
                        {opt.shortDate && (
                          <span className="ml-1 text-[14px] font-normal text-[var(--theme-text-muted)]">
                            ({opt.shortDate})
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="-mx-1 overflow-x-auto px-4">
      <div className="min-w-fit" tabIndex={-1} role="tablist" aria-label="Date tabs">
        <div className="flex items-center gap-2">
          <div className="flex h-[44px] w-full items-center rounded-2xl bg-[var(--theme-bg)]">
          {dateOptions.map((opt, idx) => {
            const isSelected = !isHotOnly && selectedDate === opt.value;
            const isLast = idx === dateOptions.length - 1;
            return (
              <div
                key={opt.value}
                className={[
                  "group relative cursor-pointer",
                  isLast ? "mr-0" : "mr-[5px]",
                  "flex h-[36px] min-w-[85px] flex-1 items-center justify-center rounded-[8px] px-0 text-[16px] font-semibold transition",
                  isSelected
                    ? "bg-[linear-gradient(284deg,var(--theme-gradient-start,var(--theme-primary)),var(--theme-tab-active,var(--theme-primary)))] text-[var(--theme-tab-text,var(--theme-on-primary,var(--theme-text)))]"
                    : "bg-[var(--theme-tab-inactive,var(--theme-accent))] text-[var(--theme-text-muted)] hover:bg-[var(--theme-btn-border,var(--theme-border))] hover:text-[var(--theme-text)]",
                ].join(" ")}
                tabIndex={isSelected ? 0 : -1}
                role="tab"
                aria-selected={isSelected ? "true" : "false"}
                onClick={() => onDateChange(opt.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onDateChange(opt.value);
                  }
                }}
              >
                <span className="relative z-10 flex h-full w-full items-center justify-center">
                  {opt.label}
                </span>
              </div>
            );
          })}
          </div>
          {typeof onToggleHot === "function" ? (
            <button
              type="button"
              onClick={onToggleHot}
              className={`inline-flex h-[44px] shrink-0 items-center gap-1 rounded-2xl border border-solid px-3 text-[14px] font-semibold ${
                isHotOnly
                  ? "border-[var(--theme-live-chip-bg,var(--theme-primary))] bg-[var(--theme-accent)] text-[var(--theme-text)]"
                  : "border-transparent bg-[var(--theme-accent)] text-[var(--theme-text)]"
              }`}
            >
              <img
                src={viugoFilterFlameIcon}
                alt=""
                width={20}
                height={20}
                className="shrink-0"
                aria-hidden
              />
              <span>Trận hot</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

